import React, { useCallback, useEffect, useRef } from "react";
import { filter } from "lodash";
import styled, { css } from "styled-components";
import {
  Elevation,
  FontSize,
  Radius,
  Spacing,
  background,
  borderRadius,
  content,
  prettyScrollbars,
  transparentize,
} from "@glossy";
import { notifyError, notifySuccess } from "@linkfluence/core/notifications";
import { Locale } from "@linkfluence/models/locale";
import { Platform } from "@linkfluence/models/platform";
import { Timezone } from "@linkfluence/models/timezone";
import { useSize } from "@lkf/hooks";
import Button from "@ui-kit/button";
import Attachment from "@radarly/components/post/attachments";
import Author from "@radarly/components/post/author";
import Content from "@radarly/components/post/content";
import Footer from "@radarly/components/post/footer";
import Menu, { PostMenuAction } from "@radarly/components/post/menu";
import Metrics from "@radarly/components/post/metrics";
import RadarlyPostQuote from "@radarly/components/post/quote";
import { useGetInfluencer } from "@radarly/core/influencer/use-cases";
import { useCurrentProjectPlatforms } from "@radarly/core/project/use-cases";
import { tr } from "@radarly/i18n";
import { Post, PostLens, PostLensFactory } from "@radarly/models/posts";
import { Project } from "@radarly/models/project";
import { Query } from "@radarly/models/queries";

// "Magic" values
const METRICS_WIDTH = 160;
const NARROW_WIDTH_THRESHOLD = 504;

interface IProps {
  post: Post;
  queries: Query[];
  project?: Project;
  timezone: Timezone;
  locale?: Locale;
  className?: string;
  isPinnedPost?: boolean; // PinnedPost should be responsive, PostList should not
  setIsNarrow?: (isNarrow: boolean) => void;
  readOnly?: boolean;
  withMetrics?: boolean;
}

const Root = styled.div<{
  hasPermalink: boolean;
  $isInsideCard: boolean;
}>`
  display: flex;
  max-height: 100%;
  width: 100%;
  background-color: ${background};
  border-radius: ${Radius.MediumLarge};

  ${(props) =>
    props.$isInsideCard
      ? `border: 1px solid rgb(187, 195, 210);`
      : css`
          box-shadow: ${Elevation.Elevation_1};
          :hover {
            box-shadow: ${props.hasPermalink && Elevation.Elevation_2};
          }
        `}
`;

const Main = styled.div<{
  hasPermalink: boolean;
}>`
  padding: ${Spacing.MediumLarge};
  cursor: ${(props) => props.hasPermalink && "pointer"};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.MediumLarge};
  flex-grow: 1;
  min-width: 0;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.Medium};
  ${prettyScrollbars};
`;

const MainContent = styled.div<{
  largePreview?: boolean;
}>`
  display: flex;
  flex-direction: ${({ largePreview }) => largePreview && "column-reverse"};
  flex-shrink: 1;
  gap: ${Spacing.Medium};

  &:hover {
    text-decoration: none;
  }
`;

const StyledRadarlyPostQuote = styled(RadarlyPostQuote)`
  color: ${content};

  :hover {
    background-color: ${(props) =>
      transparentize(0.9, content(props, "secondary"))};
  }
`;

const StyledMetrics = styled(Metrics)`
  font-size: ${FontSize.Small};
  padding: ${Spacing.Large};
  color: ${(props) => content(props, "secondary")};
  background-color: ${(props) =>
    transparentize(0.9, content(props, "primary"))};
  ${borderRadius("right", Radius.Large)};
  width: ${METRICS_WIDTH}px;
  flex-shrink: 0;
`;

const clipboardCopy = async (content: string) =>
  await navigator.clipboard.writeText(content);

const useHandleSelectMenu = (locale: Locale, lens: PostLens<Post>) => {
  const fetchInfluencer = useGetInfluencer();

  return async (id: PostMenuAction) => {
    if (id === "copy_post_id") {
      try {
        await clipboardCopy(lens.get().uid);
        return notifySuccess(tr(locale, "INSIGHT_PAGES_POST_ID_COPY_OK"));
      } catch (_) {
        return notifyError(tr(locale, "INSIGHT_PAGES_POST_ID_COPY_ERROR"));
      }
    } else if (id === "copy_author_id") {
      try {
        const influencer = await fetchInfluencer(
          lens.getPlatform(),
          lens.getInfluencerUid()
        );
        await clipboardCopy(influencer.user.uid);
        return notifySuccess(tr(locale, "INSIGHT_PAGES_AUTHOR_ID_COPY_OK"));
      } catch (e) {
        return notifyError(
          tr(locale, "INSIGHT_PAGES_AUTHOR_ID_COPY_ERROR"),
          null,
          e
        );
      }
    }
  };
};

const RadarlyPostsPost = ({
  post,
  queries,
  project,
  locale = Locale.English,
  isPinnedPost = false,
  setIsNarrow,
  timezone,
  className,
  readOnly = false,
  withMetrics = true,
}: IProps) => {
  const lens = PostLensFactory.create(post);
  const matchings = filter(queries, (query) => post.focuses.includes(query.id));
  const permalink = lens.getPermalink();
  const largePreview = !!lens.getLargePreviewData();
  const isTwitterPost = PostLens.isPlatform(Platform.Twitter, post);
  const projectPlatforms = useCurrentProjectPlatforms();
  const showViews =
    projectPlatforms.includes(Platform.Twitter) && isTwitterPost;
  const views = showViews && lens.getScores()?.impression;
  const ref = useRef();
  const { width } = useSize(ref);

  const handleSelectMenu = useHandleSelectMenu(locale, lens);

  const redirectToPermalink = useCallback(
    (event: React.MouseEvent) => {
      if (event.target instanceof Element && event.target.tagName !== "A") {
        window.open(permalink);
      }
    },
    [permalink]
  );
  const isNarrow = width < NARROW_WIDTH_THRESHOLD;
  const isSmallCard = isPinnedPost && isNarrow;

  useEffect(() => {
    setIsNarrow?.(isNarrow);
  }, [isNarrow, setIsNarrow]);

  return (
    <Root
      hasPermalink={!isPinnedPost && !!permalink}
      $isInsideCard={isPinnedPost}
      ref={ref}
      className={className}
    >
      <Main
        onClick={isPinnedPost ? () => {} : redirectToPermalink}
        hasPermalink={!isPinnedPost && !!permalink}
      >
        <Header>
          <Author
            post={post}
            locale={locale}
            timezone={timezone}
            width={width}
            showPlatform={false}
            showDetails
            forceInline
          />
          {!readOnly && (
            <Menu post={post} handleSelect={handleSelectMenu} locale={locale} />
          )}
        </Header>
        <Body>
          <MainContent largePreview={largePreview}>
            <Attachment
              post={post}
              project={project}
              width={largePreview ? 80 : 92}
              height={largePreview ? 80 : 92}
              largePreview={largePreview}
            />
            <Content
              post={post}
              parse
              limit={
                isPinnedPost
                  ? null
                  : post.highlights && post.highlights.text
                  ? 1000
                  : 280
              }
              locale={locale}
            />
          </MainContent>
          {isTwitterPost && post.quote && (
            <StyledRadarlyPostQuote
              quote={post.quote}
              locale={locale}
              timezone={timezone}
            />
          )}
        </Body>
        <Footer post={post} locale={locale} views={views} />
      </Main>
      {!isSmallCard && withMetrics && (
        <StyledMetrics
          post={post}
          locale={locale}
          matchings={matchings}
          Footer={
            isPinnedPost && (
              <Button
                variant="tertiary"
                size="xs"
                onClick={() => window.open(permalink)}
              >
                {tr(locale, `POST::GO_TO_BUTTON`)}
              </Button>
            )
          }
        />
      )}
    </Root>
  );
};

export default RadarlyPostsPost;
